/* eslint-env browser */

export const REPLAY_BLOCK_SELECTOR = [
  "[data-private]",
  "[data-replay-blocked='true']",
  ".team-select",
  ".six-credit-card",
  ".esim-activation .qr-content",
].join(", ");

const NETWORK_API_SEGMENTS = new Set([
  "assets", "advanced_search", "esim", "orders", "products", "stock_requests",
  "account", "tags", "projects", "deployments", "applications", "devices",
  "usage", "networks", "data_pools", "status", "activate", "deactivate",
]);
const NETWORK_TIMING_FIELDS = [
  "startTime", "endTime", "timeOrigin", "timestamp", "duration",
  "workerStart", "fetchStart", "domainLookupStart", "domainLookupEnd", "connectStart", "connectEnd",
  "secureConnectionStart", "requestStart", "responseStart", "responseEnd",
  "transferSize", "encodedBodySize", "decodedBodySize", "redirectStart", "redirectEnd",
  "domInteractive", "domComplete", "domContentLoadedEventStart", "domContentLoadedEventEnd",
  "loadEventStart", "loadEventEnd", "unloadEventStart", "unloadEventEnd", "redirectCount",
];

export function maskReplayNetworkRequest(request, {
  pagePath, apiRoot, routeAllowed, replayAllowed,
}) {
  if (!replayAllowed || !request || request.entryType === "serverTiming") return null;
  // The waterfall needs a document-load anchor, even when replay starts after login.
  // Keep only sanitized navigation metadata on blocked routes, never their API requests.
  if (!routeAllowed && request.entryType !== "navigation") return null;
  // The SDK also calls this hook with URL-only replay metadata.
  if (Object.keys(request).length === 1 && (request.name || request.url)) {
    return { name: `${window.location.origin}${pagePath}` };
  }

  let name;
  try {
    const url = new URL(request.name || request.url, window.location.origin);
    const api = apiRoot ? new URL(apiRoot, window.location.origin) : null;
    if (api && !api.pathname.endsWith("/")) api.pathname += "/";
    if (!["http:", "https:"].includes(url.protocol)) return null;
    if (url.origin === window.location.origin && request.entryType === "navigation") {
      name = `${url.origin}${pagePath}`;
    } else if (api && url.origin === api.origin && url.pathname.startsWith(api.pathname)) {
      const endpoint = url.pathname.slice(api.pathname.length);
      if (/^(auth|login|logout|billing|profile|developer|registry|masa|backoffice)(\/|$)/i.test(endpoint)) return null;
      const path = endpoint.split("/").map((segment) => (
        !segment || NETWORK_API_SEGMENTS.has(segment) ? segment : ":id"
      )).join("/");
      name = `${api.origin}${api.pathname}${path}`;
    } else if (url.origin === window.location.origin && /^\/(js|css|fonts)\/[\w./-]+$/.test(url.pathname)) {
      name = `${url.origin}${url.pathname}`;
    } else {
      // Third-party requests can carry credentials in their path as well as their query.
      return null;
    }
  } catch (error) {
    return null;
  }

  // Copy only known metadata; headers, bodies, server timings and future SDK fields stay out.
  const result = { name };
  NETWORK_TIMING_FIELDS.forEach((key) => {
    if (Number.isFinite(request[key])) result[key] = request[key];
  });
  if (["resource", "navigation", "paint", "first-input"].includes(request.entryType)) {
    result.entryType = request.entryType;
  }
  if (["fetch", "xmlhttprequest", "script", "css", "link", "navigation"].includes(request.initiatorType)) {
    result.initiatorType = request.initiatorType;
  }
  if (typeof request.isInitial === "boolean") result.isInitial = request.isInitial;
  const navigationType = request.navigationType || request.type;
  if (request.entryType === "navigation" && ["navigate", "reload", "back_forward", "prerender"].includes(navigationType)) {
    result.navigationType = navigationType;
  }
  if (request.entryType === "navigation" && Number.isFinite(request.domContentLoadedEventEnd)) {
    result.domContentLoadedEvent = request.domContentLoadedEventEnd;
  }
  if (/^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)$/.test(request.method || "")) result.method = request.method;
  const status = request.status || request.responseStatus;
  if (Number.isInteger(status) && status >= 100 && status <= 599) result.status = status;
  return result;
}

const PRIVATE_TEXT_SELECTOR = [
  ".ph-mask",
  ".error-message",
  "textarea",
  "option",
  "[contenteditable]:not([contenteditable='false'])",
  ".select-container .selected-items",
  ".select-container .options-wrapper",
  "#overview-balance-card h2",
  "#overview-balance-card .sub-content > .black",
  "#overview-credit-usage-card h2",
  "#overview-credit-usage-card .sub-content > .black",
  ".notification-accordion .transfer-details p b",
].join(", ");

const PRIVATE_FIELD = /(^|[\s_-])(e?mail|(first|last|full|customer|owner)[\s_-]?name|password|passwd|secret|token|api[\s_-]?key|authorization|credentials|phone|billing[\s_-]?address|credit[\s_-]?card|card[\s_-]?(number|holder|expiry|expiration)|cvv|cvc|iban)($|[\s_-])/i;
const EMAIL = /[^\s@<>"',;:()]{1,64}@[^\s@<>"',;:()]{1,253}\.[^\s@<>"',;:()]{1,63}/g;
const JWT = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;
const SECRET_VALUE = /(\b(?:password|passwd|client[_-]?secret|secret|access[_-]?token|refresh[_-]?token|token|api[\s_-]?key|authorization)\b["']?\s*[:=]\s*["']?)([^\s"'&,;<>]+)/gi;

function mask(value) {
  return value.replace(/\S/g, "*");
}

export function redactSensitiveText(value) {
  // Avoid scanning long data URLs and SVG attributes as possible email addresses.
  const sanitized = value.includes("@") ? value.replace(EMAIL, "[redacted]") : value;
  return sanitized
    .replace(JWT, "[redacted]")
    .replace(/\b(Bearer|Basic)\s+[A-Za-z0-9._~+/-]+=*/gi, "$1 [redacted]")
    .replace(SECRET_VALUE, "$1[redacted]");
}

function isPrivateField(value) {
  const key = String(value || "").replace(/([a-z])([A-Z])/g, "$1 $2");
  return PRIVATE_FIELD.test(key);
}

function isPrivateElement(element) {
  if (!element || element.closest(PRIVATE_TEXT_SELECTOR)) return true;
  // SixTable exposes column identity once; all callers share the same privacy policy.
  const cell = element.closest("td[data-field-key]");
  if (cell && isPrivateField(cell.getAttribute("data-field-key"))) return true;
  const toast = element.closest(".toast-notification .content");
  return Boolean(toast && toast.parentElement.querySelector(".title.error"));
}

// Device names and operational data stay visible; privacy rules live in this module.
export function maskReplayText(text, element) {
  if (isPrivateElement(element)) return mask(text);
  return redactSensitiveText(text);
}

export function maskReplayAttribute(name, value, element) {
  if (["href", "xlink:href"].includes(name)) {
    // Signed links can use arbitrary credential names; remove the entire query and hash.
    const path = value.split(/[?#]/, 1)[0];
    try {
      const url = new URL(path, window.location.origin);
      if (!["http:", "https:"].includes(url.protocol)) return "";
      if (path.startsWith("//")) return redactSensitiveText(`//${url.host}${url.pathname}`);
      if (/^https?:/i.test(path)) return redactSensitiveText(`${url.origin}${url.pathname}`);
      return redactSensitiveText(path);
    } catch (error) {
      return "";
    }
  }
  if (isPrivateField(name)) return mask(value);
  if (name === "value" && element && element.matches("input, textarea, select, option")) {
    return mask(value);
  }
  if (
    isPrivateElement(element)
    && ["title", "alt", "placeholder", "aria-label", "value"].includes(name)
  ) return mask(value);
  return redactSensitiveText(value);
}
